import React, { FC, Suspense, lazy, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import type {
    GetZoneQuery,
    Zone as ZoneFields,
    Matrix as MatrixFields,
    CreateZoneInput as NewNodeFields,
} from '@/generated/graphql';
import GetZone from './graphql/Zone.gql';
import StageShell from '@/components/stage/_common/StageShell/StageShell';
import { useUserCurrent } from '@/components/provider/UserCurrentProvider';
import Loading from '@/components/basic/Loading/Loading';
import { concatenateIdToPath, markContentReady } from '@/utils';
import Lite from '../_common/Lite/Lite';
import { getInitialPhotoData } from '@/components/edit/media/PhotoInput/PhotoInput';

const EditShell = lazy(
    () =>
        import(
            /* webpackChunkName: "edit-main" */ '@/components/edit/EditShell/EditShell'
        ),
);
const ZoneEdit = lazy(
    () => import(/* webpackChunkName: "edit-main" */ './ZoneEdit'),
);

type ZoneProps = {
    zoneId: number;
};

const Zone: FC<ZoneProps> = ({ zoneId }) => {
    const { userCurrent } = useUserCurrent();
    const { loading, error, data } = useQuery<GetZoneQuery>(GetZone, {
        variables: { zoneId },
    });
    useEffect(() => {
        if (!loading && !error && !!data) markContentReady();
    }, [loading, error, data]);

    if (loading) {
        return <Loading type="element" />;
    }

    if (error || !data) {
        return <pre>Zone.tsx: Error...{JSON.stringify(error, null, 2)}</pre>;
    }
    const zone: ZoneFields = data.zone;

    const canonicalPath = [
        zone.canonicalRealm[0].pathId,
        concatenateIdToPath({
            id: zone.id,
            pathId: zone.pathId,
        }),
    ];

    const currentNodes = [];
    const matrices = zone.matrices.map((matrix: MatrixFields, index) => {
        currentNodes.push({
            nodeId: matrix.id,
            title: matrix.title,
        });
        return (
            <Lite
                key={`matrix${index}`}
                fields={matrix}
                path={concatenateIdToPath({
                    id: matrix.id,
                    pathId: matrix.pathId,
                })}
            />
        );
    });

    const stageShellContent = (
        <StageShell
            componentName="Zone"
            fields={zone}
            canonicalPath={canonicalPath}
        >
            {matrices}
        </StageShell>
    );

    if (userCurrent?.isEditMode === 'ZONE') {
        const nodeIds = currentNodes.map((node) => node.nodeId);

        const passEditState = {
            editKey: zoneId,
            componentType: 'ZONE',
            fields: {
                published: zone.published,
                publishAll: false,
                autoUpdatePathId: false,
            },
            localFields: {
                title: zone.title,
                pathId: zone.pathId,
                canonicalNodeId: zone.canonicalRealm[0].id,
                bannerText: zone.bannerText,
                bannerPhoto: {
                    id: zone.bannerPhoto?.[0]?.id,
                    caption: zone.bannerPhoto?.[0]?.caption,
                    copyright: zone.bannerPhoto?.[0]?.copyright?.id,
                    copyrightUrl: zone.bannerPhoto?.[0]?.copyrightUrl,
                    imageFile: null,
                },
            },
            zoneId: zoneId,
            publishedLocked: zone.publishedLocked,
            ownerId: zone.owner.id,

            newNodeFields: {
                bannerPhoto: getInitialPhotoData(),
            } as NewNodeFields,

            // needed for deleting and sorting panels
            currentNodes: currentNodes,

            nodesFlaggedForRemoval: [],
            nodesFlaggedForUnlinking: [],

            // for sorting
            nodeIds: nodeIds, // must remain unchanged for comparison.
            sortedNodeIds: nodeIds,
            hasResortedNodes: false,
        };

        return (
            <Suspense fallback={stageShellContent}>
                <EditShell passEditState={passEditState}>
                    <ZoneEdit
                        zone={zone}
                        canonicalPath={canonicalPath}
                        matrices={matrices}
                    />
                </EditShell>
            </Suspense>
        );
    }

    return stageShellContent;
};

export default Zone;
